// Verndale Customization - Infinite Scroll, Sorting, and Table View

import CheckCircle from "@bevco/MobiusOverrides/Icons/CheckCircle";
import Quote from "@bevco/MobiusOverrides/Icons/Quote";
import ProductListProductCard, {
    ProductListProductCardStyles,
} from "@bevco/Overrides/Widgets/ProductList/ProductListProductCard";
import setCustomInfiniteScroll from "@bevco/Store/Handlers/ProductList/CustomInfiniteScroll";
import updateProductInfoProductList from "@bevco/Store/Handlers/ProductList/UpdateProductInfo";
import useCustomQuoteRequired, { isQuoteRequired } from "@bevco/Store/Handlers/Utilities/useCustomQuoteRequired";
import { CustomState } from "@bevco/Store/Reducers";
import { formatNumberWithCommas } from "@bevco/Utilities/Utilities";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import siteMessage from "@insite/client-framework/SiteMessage";
import {
    getSelectedCategoryPath,
    getSettingsCollection,
} from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCatalogPageStateByPath } from "@insite/client-framework/Store/Data/CatalogPages/CatalogPagesSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getProductsDataView } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import addProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/AddProductFilters";
import displayProducts from "@insite/client-framework/Store/Pages/ProductList/Handlers/DisplayProducts";
import { getProductListDataViewProperty } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import { AttributeTypeFacetModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import CardContainer, { CardContainerStyles } from "@insite/content-library/Components/CardContainer";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import { ProductQuantityBreakPricingStyles } from "@insite/content-library/Components/ProductQuantityBreakPricing";
import { ProductCardSelections } from "@insite/content-library/Widgets/ProductList/ProductCardSelections";
import ProductListProductContext from "@insite/content-library/Widgets/ProductList/ProductListProductContext";
import { ProductListProductImageStyles } from "@insite/content-library/Widgets/ProductList/ProductListProductImage";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Icon from "@insite/mobius/Icon";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, keyframes } from "styled-components";

interface OwnProps extends WidgetProps {}

interface DynamicAttrProps {
    [key: string]: string;
}

const mapStateToProps = (state: CustomState) => {
    // Verndale Customization
    const location = getLocation(state);
    const selectedCategoryPath = getSelectedCategoryPath(state);
    const pathnameLowerCase = location.pathname.toLowerCase();
    const path =
        selectedCategoryPath ||
        (pathnameLowerCase.startsWith("/content/") || pathnameLowerCase.startsWith("/.spire/")
            ? ""
            : location.pathname);
    const categoryPath =
        getSelectedCategoryPath(state) ||
        (location.pathname.toLowerCase().startsWith("/content/") ? "" : location.pathname);
    const catalogPageState = getCatalogPageStateByPath(state, categoryPath);

    const {
        page: initialPage,
        scrollPosition: scrollPositionState,
        isLoading: scrollIsLoading,
        products: tableProducts,
        productInfosByProductId: customProductInfosByProductId,
    } = state.customProductList;

    const dataAttributes = ["price", "available", "status"];

    let customColumnProductProperties: string[] = [];
    let customColumnProductAttributes: string[] = [];
    let showPriceInTable = false;
    let showAvailabilityInTable = false;
    let defaultSortAttribute = "";

    if (catalogPageState && catalogPageState.value) {
        customColumnProductProperties = catalogPageState.value.properties?.productProperties?.split(";~");
        customColumnProductAttributes = catalogPageState.value.properties?.productAttributes?.split(";~");

        showPriceInTable = catalogPageState.value.properties?.showPriceInTableView === "true";
        defaultSortAttribute = catalogPageState.value.properties?.defaultSortAttribute;
    }

    let tableColumns =
        getProductListDataViewProperty(state, "attributeTypeFacets")?.filter(o => {
            return customColumnProductAttributes.includes(o.name);
        }) ?? [];

    // we need to persists the order of customColumnProductAttributes

    if (customColumnProductAttributes.length > 0) {
        tableColumns.sort((a, b) => {
            return customColumnProductAttributes.indexOf(a.name) - customColumnProductAttributes.indexOf(b.name);
        });
    }

    if (customColumnProductProperties?.length > 0 && tableProducts && tableProducts.length > 0) {
        // if there is any custom column product properties, add them to the tableColumns array as first columns

        tableColumns.unshift(
            ...customColumnProductProperties
                .filter(prop => {
                    // If the prop is in the product properties object
                    // or in the product property detail object
                    // or is the erpNumber(BEVCO Part Number) or qtyOnHand(Available Quantity)
                    if (
                        (tableProducts[0] &&
                            (prop in tableProducts[0] ||
                                (tableProducts[0].detail && prop in tableProducts[0].detail))) ||
                        prop === "erpNumber"
                    ) {
                        return true;
                    }

                    if (prop === "qtyOnHand") {
                        showAvailabilityInTable = true;
                    }

                    return false;
                })
                .map(prop => {
                    let propID = prop;
                    let propNameDisplay = translate(`${prop}_column_header`);

                    if (prop === "erpNumber") {
                        propID = "part";
                        propNameDisplay = String(siteMessage("BEVCO_Part_#"));
                    }

                    dataAttributes.push(prop);

                    return {
                        attributeTypeId: propID,
                        name: propID,
                        nameDisplay: propNameDisplay,
                        attributeValueFacets: [],
                        sortOrder: 0,
                    };
                }),
        );

        // we need to allow a maximum of 15 columns, but always included the part, availability, price and status columns if any of those exists
        if (tableColumns.length > 15) {
            const mandatoryColumns = tableColumns.filter(column =>
                ["part", "availability", "price", "status"].includes(column.name),
            );
            const otherColumns = tableColumns.filter(
                column => !["part", "availability", "price", "status"].includes(column.name),
            );
            tableColumns = [...mandatoryColumns, ...otherColumns.slice(0, 15 - mandatoryColumns.length)];
        }
    }

    const productsDataView = getProductsDataView(state, state.pages.productList.parameter);

    return {
        dataAttributes,
        defaultSortAttribute,
        getProductListParameter: state.pages.productList.parameter,
        initialPage,
        isLoading: state.pages.productList.isLoading,
        path,
        productInfosByProductId: customProductInfosByProductId ?? state.pages.productList.productInfosByProductId,
        productsDataView,
        productSettings: getSettingsCollection(state).productSettings,
        scrollIsLoading,
        scrollPositionState,
        search: location.search,
        showPriceInTable,
        showAvailabilityInTable,
        tableColumns,
        tableProducts,
        isSearchPage: state.pages.productList.isSearchPage,
    };
};

const mapDispatchToProps = {
    addProductFilters,
    displayProducts,
    setCustomInfiniteScroll,
    updateProductInfoProductList,
};

type Props = OwnProps &
    ProductCardSelections &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

export interface ProductListTableStyles {
    wrapper?: InjectableCss;
    dataTable?: DataTableProps;
    attributesHeader?: DataTableHeaderProps;
    availabilityHeader?: DataTableHeaderProps;
    priceHeader?: DataTableHeaderProps;
    unitOfMeasureHeader?: DataTableHeaderProps;
    actionsHeader?: DataTableHeaderProps;
    descriptionCell?: DataTableCellProps;
    gridContainer?: GridContainerProps;
    leftColumnGridItem?: GridItemProps;
    rightColumnGridItem?: GridItemProps;
    productImageStyles?: ProductListProductImageStyles;
    attributesCell?: DataTableCellProps;
    availabilityCell?: DataTableCellProps;
    availabilityStyles?: ProductAvailabilityStyles;
    priceCell?: DataTableCellProps;
    productPrice?: ProductPriceStyles;
    quantityBreakPricing?: ProductQuantityBreakPricingStyles;
    unitOfMeasureCell?: DataTableCellProps;
    unitOfMeasureText?: TypographyPresentationProps;
    actionsCell?: DataTableCellProps;
    sponsoredText?: TypographyPresentationProps;
    // Verndale Customization
    availabilityStatusText?: TypographyPresentationProps;
    cardContainerStyles?: CardContainerStyles;
    productListProductCardStyles?: ProductListProductCardStyles;
    centeringWrapper?: InjectableCss;
    spinner?: InjectableCss;
}

export const actionsStyles: ProductListTableStyles = {
    wrapper: {
        css: css`
            overflow: auto;
        `,
    },
    gridContainer: {
        gap: 0,
        css: css`
            padding: 30px 0;
            min-width: 320px; /* prevent overlap with second column */
        `,
    },
    leftColumnGridItem: {
        width: [4, 4, 4, 3, 3],
        css: css`
            padding-right: 12px;
            flex-direction: column;
        `,
    },
    rightColumnGridItem: {
        width: [8, 8, 8, 9, 9],
        css: css`
            flex-direction: column;
        `,
    },
    productImageStyles: {
        compareCheckbox: {
            css: css`
                align-self: start;
                padding-top: 15px;
            `,
        },
    },
    actionsHeader: {
        css: css`
            position: sticky;
            right: 0;
            width: 200px;
            background-clip: padding-box;
        `,
    },
    descriptionCell: {
        css: css`
            white-space: normal;
        `,
    },
    attributesCell: {
        css: css`
            white-space: normal;
        `,
    },
    availabilityCell: {
        css: css`
            white-space: normal;
        `,
    },
    availabilityStyles: {
        messageText: {
            css: css`
                white-space: nowrap;
            `,
        },
    },
    actionsCell: {
        css: css`
            position: sticky;
            right: 0;
            width: 200px;
            background-color: inherit;
            background-clip: padding-box;
        `,
    },
    sponsoredText: {
        css: css`
            margin-bottom: 5px;
            font-size: 0.9em;
            opacity: 0.5;
        `,
    },
};

const styles = actionsStyles;

const ProductListTable = ({
    id,
    defaultSortAttribute,
    displayProducts,
    dataAttributes,
    isLoading,
    maximumNumberOfImageBadges,
    maximumNumberOfTextBadges,
    path,
    productInfosByProductId,
    search,
    showAddToList,
    showAttributes,
    showAvailability,
    showBrand,
    showCompare,
    showImage,
    showImageBadges,
    showPartNumbers,
    showPrice,
    showPriceInTable,
    showAvailabilityInTable,
    showTextBadges,
    showTitle,
    tableColumns,
    tableProducts,
    productsDataView,
    setCustomInfiniteScroll,
    isSearchPage,
    scrollIsLoading,
}: Props) => {
    const [openID, setOpenID] = useState("");
    const [isOpen, setIsOpen] = useState(false);

    const [sortedProducts, setSortedProducts] = useState<ProductModel[]>([]);
    const [sortDirections, setSortDirections] = useState({}); // Initialize as an empty object
    const topRef = useRef<HTMLDivElement | null>(null);
    const bottomRef = useRef<HTMLDivElement | null>(null);
    const [page, setPage] = useState(1);
    const [isTopVisible, setIsTopVisible] = useState(true);
    const [isBottomVisible, setIsBottomVisible] = useState(false);
    const [hasMore, setHasMore] = useState(true);

    const timeDelay = 100;

    if (!tableProducts) {
        return (
            <StyledWrapper {...styles.wrapper}>
                <StyledWrapper {...styles.centeringWrapper} data-test-selector="productListNoneFound">
                    {isSearchPage && <Zone contentId={id as never} zoneName={"Content00" as never} />}
                    {!isSearchPage && <Zone contentId={id as never} zoneName={"Content01" as never} />}
                </StyledWrapper>
            </StyledWrapper>
        );
    }

    // This useEffect hook in React triggers the loading of the next page of tableProducts
    // and updates visibility states when the page state changes.
    useEffect(() => {
        if (page > 1 && hasMore) {
            setCustomInfiniteScroll({
                page,
                isLoading: true,
            });
            displayProducts({
                path,
                queryString: `${search}&page=${page}`,
                isSearch: false,
            });
            setIsTopVisible(true);
            setIsBottomVisible(false);
        }
    }, [page]);

    // This code is a React useEffect hook that sets up Intersection Observers on topRef and bottomRef elements,
    // with a delay, to track their visibility in the viewport when product data is available and not loading.
    useEffect(() => {
        if (tableProducts && !isLoading && !scrollIsLoading) {
            const timeoutId = setTimeout(() => {
                const topObserver = new IntersectionObserver(
                    entries => {
                        setIsTopVisible(entries[0].isIntersecting);
                    },
                    { threshold: 0 },
                );

                const bottomObserver = new IntersectionObserver(
                    entries => {
                        if (entries[0].isIntersecting && document.documentElement.scrollHeight >= window.innerHeight) {
                            setIsBottomVisible(entries[0].isIntersecting);
                        }
                    },
                    {
                        threshold: 1,
                    },
                );

                if (topRef.current) {
                    topObserver.observe(topRef.current);
                }

                if (bottomRef.current) {
                    bottomObserver.observe(bottomRef.current);
                }

                return () => {
                    if (topRef.current) {
                        topObserver.unobserve(topRef.current);
                    }

                    if (bottomRef.current) {
                        bottomObserver.unobserve(bottomRef.current);
                    }
                };
            }, timeDelay);

            return () => clearTimeout(timeoutId);
        }
    }, [topRef, bottomRef, tableProducts, isLoading, scrollIsLoading]);

    // Load next page only when not already loading (avoids overlapping scroll requests during sort).
    useEffect(() => {
        if (tableProducts && !isTopVisible && isBottomVisible && !isLoading && !scrollIsLoading && hasMore) {
            const timeoutId = setTimeout(() => {
                setPage(prevPage => prevPage + 1);
            }, timeDelay);

            return () => clearTimeout(timeoutId);
        }
    }, [isTopVisible, isBottomVisible, tableProducts, isLoading, scrollIsLoading, hasMore]);

    // Best Match. Catalog defaultSortAttribute (e.g. Gauge Size) should only apply for this.
    const BEST_MATCH_SORT = "1";
    // Brand: A to Z / Z to A — must sort Manufacturer/brand, not the catalog default column.
    const BRAND_SORT_ASC = "9";
    const BRAND_SORT_DESC = "10";

    const getManufacturerSortValue = (product: ProductModel) => {
        const fromAttribute = product.attributeTypes
            ?.find(attribute => attribute.name === "Manufacturer" || attribute.name === "brand")
            ?.attributeValues?.map(o => o.valueDisplay)
            .join(", ");
        return (
            fromAttribute ||
            product.properties?.manufacturerNameValue ||
            product.brand?.name ||
            ""
        );
    };

    const getSortValueForField = (product: ProductModel, sortField: string) => {
        if (sortField === "Manufacturer" || sortField === "brand") {
            return getManufacturerSortValue(product);
        }

        const attribute = product.attributeTypes?.find((a: { name: string }) => a.name === sortField);
        if (attribute?.attributeValues?.[0]) {
            return attribute.attributeValues[0].value ?? attribute.attributeValues[0].valueDisplay ?? "";
        }

        return "";
    };

    const compareSortValues = (valueA: string | number, valueB: string | number, direction: string) => {
        const aIsNumeric = typeof valueA === "number" || (!Number.isNaN(Number(valueA)) && valueA !== "" && !String(valueA).includes("/"));
        const bIsNumeric = typeof valueB === "number" || (!Number.isNaN(Number(valueB)) && valueB !== "" && !String(valueB).includes("/"));

        if (aIsNumeric && bIsNumeric) {
            const numA = Number(valueA);
            const numB = Number(valueB);
            return direction === "ascending" ? numA - numB : numB - numA;
        }

        const strA = String(valueA ?? "");
        const strB = String(valueB ?? "");
        return direction === "ascending"
            ? strA.localeCompare(strB, undefined, { numeric: true, sensitivity: "base" })
            : strB.localeCompare(strA, undefined, { numeric: true, sensitivity: "base" });
    };

    const sortProducts = (productsToSort: ProductModel[], sortField: string, direction: string) => {
        const productsCopy = [...productsToSort];

        if (dataAttributes.includes(sortField.toLowerCase()) || dataAttributes.includes(sortField)) {
            const attribute = `data-${sortField.toLowerCase()}`;
            const rows = Array.from(document.querySelectorAll("[data-test-selector^='productListProductCard_']"));
            rows.sort((a, b) => {
                const aValue = a.getAttribute(attribute) || "";
                const bValue = b.getAttribute(attribute) || "";

                const isNumeric = !Number.isNaN(parseFloat(aValue)) && Number.isFinite(parseFloat(bValue));

                if (isNumeric) {
                    return direction === "ascending"
                        ? parseFloat(aValue) - parseFloat(bValue)
                        : parseFloat(bValue) - parseFloat(aValue);
                }
                return direction === "ascending"
                    ? aValue.localeCompare(bValue, undefined, { numeric: true })
                    : bValue.localeCompare(aValue, undefined, { numeric: true });
            });

            const sorted = rows.map(row => {
                const id = row.getAttribute("data-test-selector")?.replace("productListProductCard_", "");
                return productsToSort.find(product => product.id === id);
            });

            // Keep any products not yet in the DOM (e.g. just appended via infinite scroll)
            const sortedIds = new Set(sorted.filter(Boolean).map(p => p!.id));
            const missing = productsToSort.filter(p => !sortedIds.has(p.id));
            return [...(sorted.filter(Boolean) as ProductModel[]), ...missing];
        }

        if (sortField === "Manufacturer" || sortField === "brand") {
            return productsCopy.sort((productA, productB) =>
                compareSortValues(
                    getManufacturerSortValue(productA),
                    getManufacturerSortValue(productB),
                    direction,
                ),
            );
        }

        return productsCopy.sort((productA, productB) => {
            const valueA = getSortValueForField(productA, sortField);
            const valueB = getSortValueForField(productB, sortField);

            if (valueA === "" && valueB === "") {
                return 0;
            }

            const normalizedA = Number.isNaN(Number(valueA)) || String(valueA).includes("/") ? valueA : Number(valueA);
            const normalizedB = Number.isNaN(Number(valueB)) || String(valueB).includes("/") ? valueB : Number(valueB);

            return compareSortValues(normalizedA, normalizedB, direction);
        });
    };

    const dropdownSortType =
        productsDataView && "pagination" in productsDataView
            ? productsDataView.pagination?.sortType
            : undefined;

    // Reset infinite-scroll page cursor when the Sort by dropdown (URL search) changes
    useEffect(() => {
        setPage(1);
        setHasMore(true);
        setSortDirections({});
        setIsTopVisible(true);
        setIsBottomVisible(false);
    }, [search]);

    useEffect(() => {
        if (!tableProducts) {
            setSortedProducts([]);
            return;
        }

        // Brand dropdown: sort loaded rows by manufacturer/brand (matches Manufacturer column)
        if (dropdownSortType === BRAND_SORT_ASC || dropdownSortType === BRAND_SORT_DESC) {
            const direction = dropdownSortType === BRAND_SORT_ASC ? "ascending" : "descending";
            setSortDirections({ Manufacturer: direction });
            setSortedProducts(sortProducts(tableProducts, "Manufacturer", direction));
            return;
        }

        // Best Match (or unset): apply catalog default column sort (e.g. Gauge Size)
        const shouldApplyDefaultColumnSort =
            !!defaultSortAttribute && (!dropdownSortType || dropdownSortType === BEST_MATCH_SORT);

        if (shouldApplyDefaultColumnSort) {
            const currentDirection =
                sortDirections[defaultSortAttribute as keyof typeof sortDirections] ?? "ascending";

            setSortDirections({ [defaultSortAttribute]: currentDirection });
            setSortedProducts(sortProducts(tableProducts, defaultSortAttribute, currentDirection));
            return;
        }

        // Other dropdown sorts (Product A-Z, etc.): keep API order
        setSortedProducts(tableProducts);
        setSortDirections({});
    }, [tableProducts, dropdownSortType, defaultSortAttribute]);

    useEffect(() => {
        if (
            productsDataView &&
            "pagination" in productsDataView &&
            productsDataView.pagination!.currentPage === productsDataView.pagination!.numberOfPages
        ) {
            setHasMore(false);
        }
    }, [tableProducts]);

    const getDataCellValue = (facet: AttributeTypeFacetModel, product: ProductModel) => {
        const attributeValues = product.attributeTypes
            ?.find(o => o.id === facet.attributeTypeId)
            ?.attributeValues?.map(o => o.valueDisplay)
            .join(", ");
        if (attributeValues) {
            return attributeValues;
        }

        switch (facet.name) {
            case "part":
                return product.properties?.isCmiProduct === "true"
                    ? product.detail?.modelNumber
                    : product.productNumber;

            case "Manufacturer":
            case "brand":
                return getManufacturerSortValue(product) || null;

            case "productLine":
                if (product.productLine) {
                    return product.productLine.name;
                }
                break;

            default:
                if (facet.name in product) {
                    return (product as any)[facet.name].toString();
                }

                if (product.detail && facet.name in product.detail) {
                    return (product.detail as any)[facet.name].toString();
                }
                return null;
        }
    };

    const headerSortClick = (attributeTypeId: string) => {
        const currentDirection = sortDirections[attributeTypeId as keyof typeof sortDirections] ?? "ascending";

        setSortDirections({
            ...sortDirections,
            [attributeTypeId]: currentDirection === "ascending" ? "descending" : "ascending",
        });
        const sorted = sortProducts(tableProducts, attributeTypeId, currentDirection);
        setSortedProducts(sorted);
    };

    const getPriceDetails = (productInfo: ProductInfo, showPriceInTable: boolean) => {
        if (!showPriceInTable || !productInfo?.pricing) {
            return { priceDisplay: "", price: 0 };
        }

        const pricing = productInfo.pricing.actualBreakPrices?.[0]?.breakPrice || productInfo.pricing.unitNetPrice || 0;
        const priceDisplay = pricing
            ? `$${pricing.toFixed(5)} ${
                  productInfo.pricing.unitOfMeasure ? ` / ${productInfo.pricing.unitOfMeasure}` : ""
              }`
            : "";
        const price = +pricing.toFixed(5) || 0;

        return { priceDisplay, price };
    };

    const getAvailabilityDetails = (productInfo: ProductInfo, showAvailabilityInTable: boolean) => {
        if (
            !showAvailabilityInTable ||
            !productInfo?.pricing ||
            !productInfo?.inventory ||
            productInfo.inventory.qtyOnHand <= 0
        ) {
            return { availabilityDisplay: "", availability: 0 };
        }

        const unitOfMeasure = productInfo.pricing?.unitOfMeasure || "";
        const availabilityDisplay = `${formatNumberWithCommas(productInfo.inventory.qtyOnHand)} ${unitOfMeasure}`;
        const availability = productInfo.inventory.qtyOnHand;

        return { availabilityDisplay, availability };
    };

    const rows = sortedProducts!.map((product, idx) => {
        const productNumber =
            product.properties?.isCmiProduct === "true" ? product.detail?.modelNumber : product.productNumber;
        const productInfo = productInfosByProductId![product.id];

        if (!productInfo) {
            return null;
        }

        const { priceDisplay, price } = getPriceDetails(productInfo, showPriceInTable);
        const { availabilityDisplay, availability } = getAvailabilityDetails(productInfo, showAvailabilityInTable);
        const quoteRequired = productInfo ? isQuoteRequired({ product, productInfo }) : product.quoteRequired;

        const color = quoteRequired ? "info" : "success";
        const iconSrc = quoteRequired ? Quote : CheckCircle;

        const toggleRow = (id: string) => {
            if (openID === id) {
                setOpenID("");
                setIsOpen(false);
            } else {
                setOpenID(id);
                setIsOpen(true);
            }
        };

        // Prepare dynamic data attributes
        const dynamicDataAttributes: DynamicAttrProps = {};
        for (const key of dataAttributes) {
            if (key === "price") {
                dynamicDataAttributes[`data-${key}`] = price.toString();
            }

            if (key === "available") {
                dynamicDataAttributes[`data-${key}`] = availability.toString();
            }

            if (key === "status") {
                dynamicDataAttributes[`data-${key}`] = quoteRequired ? "quote" : "buy";
            }

            if (key === "part") {
                dynamicDataAttributes[`data-${key}`] = productNumber!;
            }

            // else the attribute is on the product properties
            if (key in product || (product.detail && key in product.detail) || key === "Manufacturer") {
                switch (key) {
                    case "Manufacturer":
                    case "brand":
                        dynamicDataAttributes[`data-${key.toLowerCase()}`] = getManufacturerSortValue(product);
                        break;
                    case "productLine":
                        dynamicDataAttributes[`data-${key.toLowerCase()}`] = (product as any).productLine?.name ?? "";
                        break;
                    default:
                        if (product.detail && key in product.detail) {
                            dynamicDataAttributes[`data-${key.toLowerCase()}`] = (product.detail as any)[key] as string;
                        } else {
                            dynamicDataAttributes[`data-${key.toLowerCase()}`] = (product as any)[key] as string;
                        }
                        break;
                }
            }
        }

        return (
            <React.Fragment key={product.id}>
                <DataTableRow
                    data-test-selector={`productListProductCard_${product.id}`}
                    onClick={() => toggleRow(product.id)}
                    aria-expanded={openID === product.id && isOpen}
                    aria-controls={`product-accordion-content-${product.id}`}
                    id={`product-accordion-header-${product.id}`}
                    className={`product-accordion-header ${idx % 2 === 0 ? "even" : "odd"}`}
                    {...dynamicDataAttributes}
                >
                    {tableColumns.map(facet => (
                        <DataTableCell
                            key={`${facet.name}_tableCell`}
                            {...styles.attributesCell}
                            data-test-selector={`attributeValuesFor_${facet.attributeTypeId}`}
                        >
                            {getDataCellValue(facet, product)}
                        </DataTableCell>
                    ))}

                    {showPriceInTable && (
                        <DataTableCell {...styles.priceCell}>
                            {priceDisplay || quoteRequired ? (
                                <Typography>{priceDisplay}</Typography>
                            ) : null
                            // <LoadingSpinner size={16}></LoadingSpinner>
                            }
                        </DataTableCell>
                    )}

                    {showAvailabilityInTable && (
                        <DataTableCell {...styles.availabilityCell}>
                            {availabilityDisplay || quoteRequired ? (
                                <Typography>{availabilityDisplay}</Typography>
                            ) : null
                            // <LoadingSpinner size={16}></LoadingSpinner>
                            }
                        </DataTableCell>
                    )}

                    <DataTableCell {...styles.availabilityCell}>
                        <Typography color={color} {...styles.availabilityStatusText}>
                            <Icon color={color} src={iconSrc} />
                            <span data-test-selector="availabilityMessage">
                                {quoteRequired ? translate("Quote") : translate("Buy Now")}
                            </span>
                        </Typography>
                    </DataTableCell>
                </DataTableRow>

                <ProductListProductContext product={product} idx={idx}>
                    <DataTableRow
                        className="product-accordion-content"
                        id={`product-accordion-content-${product.id}`}
                        data-test-selector={`productListProductAccordionContent${product.id}`}
                        aria-labelledby={`product-accordion-header-${product.id}`}
                        aria-hidden={openID !== product.id}
                    >
                        <DataTableCell colSpan={100} style={{ padding: 0 }}>
                            <CardContainer extendedStyles={styles.cardContainerStyles}>
                                <ProductListProductCard
                                    showAvailability={showAvailability}
                                    showBrand={showBrand}
                                    showPartNumbers={showPartNumbers}
                                    showTitle={showTitle}
                                    showAddToList={showAddToList}
                                    showImageBadges={showImageBadges}
                                    maximumNumberOfImageBadges={maximumNumberOfImageBadges}
                                    showTextBadges={showTextBadges}
                                    maximumNumberOfTextBadges={maximumNumberOfTextBadges}
                                    showAttributes={showAttributes}
                                    showImage={showImage}
                                    showCompare={showCompare}
                                    showPrice={showPrice}
                                    {...styles.productListProductCardStyles}
                                />
                            </CardContainer>
                        </DataTableCell>
                    </DataTableRow>
                </ProductListProductContext>
            </React.Fragment>
        );
    });

    return (
        <StyledWrapper {...styles.wrapper}>
            <div ref={topRef}></div>
            <DataTable {...styles.dataTable}>
                <DataTableHead>
                    {tableColumns.map(column => {
                        return (
                            <DataTableHeader
                                sorted={sortDirections[column.name as keyof typeof sortDirections]}
                                onSortClick={() => headerSortClick(column.name)}
                                key={`${column.name}_tableHeader`}
                                {...styles.attributesHeader}
                            >
                                {column.nameDisplay}
                            </DataTableHeader>
                        );
                    })}
                    {showPriceInTable && (
                        <DataTableHeader
                            sorted={sortDirections["Price" as keyof typeof sortDirections]}
                            onSortClick={() => headerSortClick("Price")}
                            {...styles.attributesHeader}
                        >
                            {translate("Price Per")}
                        </DataTableHeader>
                    )}

                    {showAvailabilityInTable && (
                        <DataTableHeader
                            {...styles.attributesHeader}
                            sorted={sortDirections["Available" as keyof typeof sortDirections]}
                            onSortClick={() => headerSortClick("Available")}
                        >
                            {translate("Available Quantity")}
                        </DataTableHeader>
                    )}

                    <DataTableHeader
                        {...styles.attributesHeader}
                        sorted={sortDirections["Status" as keyof typeof sortDirections]}
                        onSortClick={() => headerSortClick("Status")}
                    >
                        {translate("Status")}
                    </DataTableHeader>
                </DataTableHead>

                <DataTableBody>
                    {rows}

                    {isLoading && (
                        <DataTableRow style={{ border: 0 }}>
                            <DataTableCell colSpan={100}>
                                <StyledWrapper {...styles.centeringWrapper}>
                                    <LoadingSpinner
                                        {...styles.spinner}
                                        data-test-selector="productListCardListSpinner"
                                    />
                                </StyledWrapper>
                            </DataTableCell>
                        </DataTableRow>
                    )}
                </DataTableBody>
            </DataTable>
            {!isLoading && <div ref={bottomRef}></div>}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductListTable as any) as any;
